import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, ChevronRight, ChevronLeft,
  FileText, AlertTriangle, List, HelpCircle, Shield,
  MessageSquare, ClipboardList, Plus, X, CheckCircle2, Download,
  FolderLock, Check, Cloud, CloudOff,
} from "lucide-react";
import axios from "axios";
import SaveToCaseLockerModal from "../components/SaveToCaseLockerModal";
import LinkedCaseChip from "../components/LinkedCaseChip";

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Editable list field ─── */
import {
  ListField, Field, StepBar, StatusBadge, SaveStateIndicator,
  STEPS, computeStepProgress, computeOverallProgress,
} from './wizard/wizardBits';
import {
  splitCsvGB, buildGrievancePeople, inferGrievanceIssueType,
  ReviewSection, ReviewRow, ReviewList,
} from './wizard/reviewHelpers';
import { wizardStyles } from './wizard/styles';


export default function GrievanceBuilderPage({ user }) {
  const navigate = useNavigate();
  const { draftId: paramDraftId } = useParams();
  const [searchParams] = useSearchParams();
  const ffId = searchParams.get("from_ff");

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | dirty | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [currentDraftId, setCurrentDraftId] = useState(paramDraftId || null);
  const [loading, setLoading] = useState(false);
  const [showCaseLockerModal, setShowCaseLockerModal] = useState(false);
  const [linkedRefreshKey, setLinkedRefreshKey] = useState(0);
  const skipNextAutoSaveRef = useRef(true); // don't autosave on initial mount/load

  const [form, setForm] = useState({
    grievance_title: "", source_fact_finder_id: "",
    employee_name: "", department: "", location: "",
    incident_date: "", meeting_date: "", supervisor_name: "",
    what_happened: "", instruction_given: "", employee_response: "",
    other_people: "", witnesses: "", documents_available: "",
    prior_related_events: "", safety_concerns: "", contract_concerns: "", notes: "",
    issue_summary: "",
    possible_issue_areas: [], supporting_facts: [], timeline: [],
    missing_information: [], questions_to_clarify: [],
    requested_remedy_ideas: [], steward_talking_points: [],
    steward_notes: "", status: "draft",
  });

  const handleChange = useCallback((e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const setListField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Load existing draft
  useEffect(() => {
    if (paramDraftId) { loadDraft(paramDraftId); return; }
    if (ffId) { createFromFF(ffId); return; }
    // No route param → try to rehydrate the user's last-saved draft from localStorage.
    const storedId = localStorage.getItem('gb_last_draft_id');
    if (storedId) loadDraft(storedId, { silent: true });
  }, [paramDraftId, ffId]);

  async function loadDraft(id, { silent = false } = {}) {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/grievance-builder/drafts/${id}`);
      populateForm(data);
      setCurrentDraftId(id);
      // Populate save state so users don't see a stale "Not saved yet" chip.
      setSaveState("saved");
      const updated = data.updated_at || data.updatedAt;
      setLastSavedAt(updated ? new Date(updated) : new Date());
      // The populateForm call sets `form`, which would normally trigger the
      // auto-save effect on the next render. Skip it — we just loaded fresh.
      skipNextAutoSaveRef.current = true;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404 || status === 400) {
        // Stored draft was deleted server-side, or the id was malformed.
        // Either way it can never resolve — drop the cache so we don't keep firing.
        localStorage.removeItem('gb_last_draft_id');
      } else if (!silent) {
        setSaveMsg("Unable to load draft.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function createFromFF(id) {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/grievance-builder/from-fact-finder/${id}`);
      populateForm(data);
      setCurrentDraftId(data.id);
      setSaveMsg("Prefilled from Fact Finder case.");
    } catch {
      setSaveMsg("Unable to load Fact Finder case.");
    } finally {
      setLoading(false);
    }
  }

  function populateForm(d) {
    setForm({
      grievance_title: d.grievance_title || "",
      source_fact_finder_id: d.source_fact_finder_id || "",
      employee_name: d.employee_name || "",
      department: d.department || "",
      location: d.location || "",
      incident_date: d.incident_date || "",
      meeting_date: d.meeting_date || "",
      supervisor_name: d.supervisor_name || "",
      what_happened: d.what_happened || "",
      instruction_given: d.instruction_given || "",
      employee_response: d.employee_response || "",
      other_people: d.other_people || "",
      witnesses: d.witnesses || "",
      documents_available: d.documents_available || "",
      prior_related_events: d.prior_related_events || "",
      safety_concerns: d.safety_concerns || "",
      contract_concerns: d.contract_concerns || "",
      notes: d.notes || "",
      issue_summary: d.issue_summary || "",
      possible_issue_areas: d.possible_issue_areas || [],
      supporting_facts: d.supporting_facts || [],
      timeline: d.timeline || [],
      missing_information: d.missing_information || [],
      questions_to_clarify: d.questions_to_clarify || [],
      requested_remedy_ideas: d.requested_remedy_ideas || [],
      steward_talking_points: d.steward_talking_points || [],
      steward_notes: d.steward_notes || "",
      status: d.status || "draft",
    });
  }

  function exportPdf() {
    if (!currentDraftId) {
      setSaveMsg("Save your draft first before exporting PDF.");
      return;
    }
    window.open(`${API}/api/grievance-builder/drafts/${currentDraftId}/pdf`, "_blank");
  }

  async function saveDraft({ silent = false } = {}) {
    if (!silent) setSaving(true);
    setSaveState("saving");
    if (!silent) setSaveMsg("");
    try {
      if (currentDraftId) {
        await axios.put(`${API}/api/grievance-builder/drafts/${currentDraftId}`, form);
        if (!silent) setSaveMsg("Draft updated.");
      } else {
        // Only auto-create a new draft if the user has entered any real content.
        const hasVal = (v) => typeof v === "string" && v.trim().length > 0;
        const hasContent = hasVal(form.grievance_title) || hasVal(form.what_happened) || hasVal(form.issue_summary);
        if (!hasContent) { setSaveState("idle"); return; }
        const { data } = await axios.post(`${API}/api/grievance-builder/save`, form);
        setCurrentDraftId(data.id);
        if (!silent) setSaveMsg("Draft saved.");
      }
      setSaveState("saved");
      setLastSavedAt(new Date());
    } catch {
      setSaveState("error");
      if (!silent) setSaveMsg("Unable to save.");
    } finally {
      if (!silent) setSaving(false);
    }
  }

  // Overall progress % — used by the header and hero indicator.
  const overallProgress = useMemo(() => computeOverallProgress(form), [form]);

  // ─── Auto-save: debounce 1200ms after any form change ─────────────────
  useEffect(() => {
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }
    setSaveState((s) => (s === "saving" ? s : "dirty"));
    const t = setTimeout(() => { saveDraft({ silent: true }); }, 1200);
    return () => clearTimeout(t);
  }, [form]);

  // When switching drafts or first-loading, reset the "skip next" gate.
  useEffect(() => { skipNextAutoSaveRef.current = true; }, [paramDraftId]);

  // Remember the current draftId in localStorage so a page reload can re-attach.
  useEffect(() => {
    if (currentDraftId) {
      try { localStorage.setItem('gb_last_draft_id', currentDraftId); } catch { /* quota */ }
    }
  }, [currentDraftId]);

  if (loading) {
    return (
      <div className="gb-page" data-testid="grievance-builder-page">
        <style>{wizardStyles}</style>
        <div className="gb-loading"><Loader2 size={24} className="gb-spin" /> Loading...</div>
      </div>
    );
  }

  return (
    <div className="gb-page" data-testid="grievance-builder-page">
      <style>{wizardStyles}</style>

      {/* Header */}
      <header className="gb-pg-header">
        <div>
          <span className="gb-badge">Steward Prep</span>
          <h1>Grievance Builder</h1>
          <p>Organize facts, identify possible issue areas, and prepare for steward review. This is not legal advice.</p>
        </div>
        <SaveStateIndicator state={saveState} lastSavedAt={lastSavedAt} />
      </header>

      <StepBar current={step} form={form} onJump={setStep} />

      <div className="gb-overall-progress" data-testid="gb-overall-progress" aria-label={`Overall progress ${Math.round(overallProgress * 100)}%`}>
        <div className="gb-overall-progress-fill" style={{ width: `${Math.round(overallProgress * 100)}%` }} />
        <span className="gb-overall-progress-label">{Math.round(overallProgress * 100)}% complete</span>
      </div>

      {saveMsg && <div className="gb-save-msg" data-testid="save-message">{saveMsg}</div>}

      {/* Step 1: Case Info */}
      {step === 1 && (
        <div className="gb-card" data-testid="step-1">
          <h2>Case Information</h2>
          <p className="gb-hint">Identify the grievance and the people involved.</p>
          <Field label="Grievance Title" name="grievance_title" value={form.grievance_title} onChange={handleChange} placeholder="e.g., Directed to skip LOTO procedure" />
          {form.source_fact_finder_id && (
            <div className="gb-ff-link">Linked to Fact Finder case <code>{form.source_fact_finder_id}</code></div>
          )}
          <div className="gb-grid">
            <Field label="Employee Name" name="employee_name" value={form.employee_name} onChange={handleChange} placeholder="Full name" />
            <Field label="Department" name="department" value={form.department} onChange={handleChange} placeholder="e.g., Substation Maintenance" />
            <Field label="Location" name="location" value={form.location} onChange={handleChange} placeholder="e.g., Station 42" />
            <Field label="Supervisor" name="supervisor_name" value={form.supervisor_name} onChange={handleChange} placeholder="Who directed or questioned you?" />
            <Field label="Incident Date" name="incident_date" value={form.incident_date} onChange={handleChange} placeholder="YYYY-MM-DD" />
            <Field label="Meeting Date" name="meeting_date" value={form.meeting_date} onChange={handleChange} placeholder="Upcoming meeting date" />
          </div>
          <div className="gb-nav">
            <div />
            <button className="gb-next" onClick={() => setStep(2)} data-testid="next-btn">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 2: Narrative */}
      {step === 2 && (
        <div className="gb-card" data-testid="step-2">
          <h2>What Happened</h2>
          <p className="gb-hint">Document what occurred, any instructions given, and how the employee responded.</p>
          <Field label="Issue Summary" name="issue_summary" value={form.issue_summary} onChange={handleChange} placeholder="Brief summary of the possible issue..." multiline />
          <Field label="What Happened" name="what_happened" value={form.what_happened} onChange={handleChange} placeholder="Describe the event in your own words..." multiline />
          <Field label="Instruction Given" name="instruction_given" value={form.instruction_given} onChange={handleChange} placeholder="Exact words if possible" multiline />
          <Field label="Employee Response" name="employee_response" value={form.employee_response} onChange={handleChange} placeholder="What you did or said" multiline />
          <div className="gb-grid">
            <Field label="Other People Involved" name="other_people" value={form.other_people} onChange={handleChange} placeholder="Names and roles" />
            <Field label="Witnesses" name="witnesses" value={form.witnesses} onChange={handleChange} placeholder="Anyone who saw or heard" />
          </div>
          <div className="gb-nav">
            <button className="gb-prev" onClick={() => setStep(1)}><ChevronLeft size={16} /> Back</button>
            <button className="gb-next" onClick={() => setStep(3)} data-testid="next-btn">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 3: Analysis */}
      {step === 3 && (
        <div className="gb-card" data-testid="step-3">
          <h2>Possible Issue Areas &amp; Facts</h2>
          <p className="gb-hint">Identify areas that may require review and the facts currently documented.</p>
          <ListField label="Possible Issue Areas" items={form.possible_issue_areas} onChange={(v) => setListField("possible_issue_areas", v)} placeholder="e.g., Safety procedure may have been bypassed" />
          <ListField label="Supporting Facts" items={form.supporting_facts} onChange={(v) => setListField("supporting_facts", v)} placeholder="e.g., Employee was not provided proper PPE" />
          <ListField label="Timeline" items={form.timeline} onChange={(v) => setListField("timeline", v)} placeholder="e.g., 8:00 AM — Arrived at station" />
          <Field label="Documents / Evidence" name="documents_available" value={form.documents_available} onChange={handleChange} placeholder="Emails, texts, photos, records" multiline />
          <Field label="Prior Related Events" name="prior_related_events" value={form.prior_related_events} onChange={handleChange} placeholder="Earlier conversations or similar incidents" multiline />
          <div className="gb-nav">
            <button className="gb-prev" onClick={() => setStep(2)}><ChevronLeft size={16} /> Back</button>
            <button className="gb-next" onClick={() => setStep(4)} data-testid="next-btn">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 4: Steward Prep */}
      {step === 4 && (
        <div className="gb-card" data-testid="step-4">
          <h2>Steward Preparation</h2>
          <p className="gb-hint">Prepare talking points, clarification questions, and remedy ideas for steward review.</p>
          <Field label="Safety Concerns" name="safety_concerns" value={form.safety_concerns} onChange={handleChange} placeholder="Any safety issue involved?" multiline />
          <Field label="Contract / Fairness Concerns" name="contract_concerns" value={form.contract_concerns} onChange={handleChange} placeholder="Any contract violation or unfair treatment?" multiline />
          <ListField label="Missing Information" items={form.missing_information} onChange={(v) => setListField("missing_information", v)} placeholder="e.g., Need to confirm exact time of directive" />
          <ListField label="Questions to Clarify" items={form.questions_to_clarify} onChange={(v) => setListField("questions_to_clarify", v)} placeholder="e.g., Was the employee given a chance to respond?" />
          <ListField label="Requested Remedy Ideas" items={form.requested_remedy_ideas} onChange={(v) => setListField("requested_remedy_ideas", v)} placeholder="e.g., Rescind written warning" />
          <ListField label="Steward Talking Points" items={form.steward_talking_points} onChange={(v) => setListField("steward_talking_points", v)} placeholder="e.g., Employee followed standard procedure" />
          <Field label="Steward Notes" name="steward_notes" value={form.steward_notes} onChange={handleChange} placeholder="Private notes for steward use..." multiline />
          <div className="gb-nav">
            <button className="gb-prev" onClick={() => setStep(3)}><ChevronLeft size={16} /> Back</button>
            <button className="gb-next" onClick={() => setStep(5)} data-testid="next-btn">Review <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 5: Review */}
      {step === 5 && (
        <div className="gb-card" data-testid="step-5-review">
          <div className="gb-review-head">
            <h2>Review &amp; Save</h2>
            <LinkedCaseChip
              sourceType="grievance"
              sourceId={currentDraftId}
              refreshKey={linkedRefreshKey}
            />
          </div>
          <p className="gb-hint">Review your grievance preparation, set the status, and save.</p>

          <StatusBadge status={form.status} onStatusChange={(v) => setForm((prev) => ({ ...prev, status: v }))} />

          <div className="gb-review-sections">
            <ReviewSection title="Case Info" icon={FileText}>
              <ReviewRow label="Title" value={form.grievance_title} />
              <ReviewRow label="Employee" value={form.employee_name} />
              <ReviewRow label="Department" value={form.department} />
              <ReviewRow label="Location" value={form.location} />
              <ReviewRow label="Incident Date" value={form.incident_date} />
              <ReviewRow label="Supervisor" value={form.supervisor_name} />
            </ReviewSection>

            <ReviewSection title="Narrative" icon={MessageSquare}>
              <ReviewRow label="Issue Summary" value={form.issue_summary} />
              <ReviewRow label="What Happened" value={form.what_happened} />
              <ReviewRow label="Instruction Given" value={form.instruction_given} />
              <ReviewRow label="Employee Response" value={form.employee_response} />
            </ReviewSection>

            <ReviewSection title="Possible Issue Areas" icon={AlertTriangle} accentColor="var(--color-danger)">
              <ReviewList items={form.possible_issue_areas} emptyText="None added yet" />
            </ReviewSection>

            <ReviewSection title="Supporting Facts" icon={Shield} accentColor="#22d3ee">
              <ReviewList items={form.supporting_facts} emptyText="None added yet" />
            </ReviewSection>

            <ReviewSection title="Timeline" icon={List}>
              <ReviewList items={form.timeline} ordered emptyText="No timeline entries" />
            </ReviewSection>

            <ReviewSection title="Missing Information" icon={HelpCircle} accentColor="#f59e0b">
              <ReviewList items={form.missing_information} emptyText="None identified" />
            </ReviewSection>

            <ReviewSection title="Questions to Clarify" icon={HelpCircle} accentColor="#a78bfa">
              <ReviewList items={form.questions_to_clarify} emptyText="None added" />
            </ReviewSection>

            <ReviewSection title="Requested Remedy Ideas" icon={ClipboardList} accentColor="#22c55e">
              <ReviewList items={form.requested_remedy_ideas} emptyText="None added" />
            </ReviewSection>

            <ReviewSection title="Steward Talking Points" icon={Shield} accentColor="#818cf8">
              <ReviewList items={form.steward_talking_points} emptyText="None added" />
            </ReviewSection>

            {form.steward_notes && (
              <ReviewSection title="Steward Notes" icon={MessageSquare}>
                <p className="gb-review-text">{form.steward_notes}</p>
              </ReviewSection>
            )}
          </div>

          <div className="gb-disclaimer" data-testid="disclaimer">
            This document is a preparation tool for steward review. It does not constitute legal advice and should not be treated as such. All content may require further review.
          </div>

          <div className="gb-actions" data-testid="review-actions">
            <button className="gb-prev" onClick={() => setStep(4)}><ChevronLeft size={16} /> Back</button>
            <div className="gb-actions-right">
              <button onClick={saveDraft} disabled={saving} className="gb-btn-save" data-testid="save-btn">
                {saving ? <><Loader2 size={14} className="gb-spin" /> Saving...</> : <><Save size={14} /> Save Grievance Prep</>}
              </button>
              <button onClick={exportPdf} className="gb-btn-pdf" data-testid="export-pdf-btn">
                <Download size={14} /> Export PDF
              </button>
              {currentDraftId && (
                <button
                  onClick={() => setShowCaseLockerModal(true)}
                  className="gb-btn-case"
                  data-testid="save-to-case-locker-btn"
                >
                  <FolderLock size={14} /> Save to Case Locker
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <SaveToCaseLockerModal
        open={showCaseLockerModal}
        onClose={() => setShowCaseLockerModal(false)}
        onSaved={() => setLinkedRefreshKey(k => k + 1)}
        sourceType="grievance"
        sourceId={currentDraftId}
        prefill={{
          title: form.grievance_title
            ? `Grievance: ${form.grievance_title.slice(0, 60)}`
            : `Grievance – ${form.incident_date || new Date().toISOString().slice(0, 10)}`,
          summary:
            form.issue_summary ||
            form.what_happened ||
            (form.supporting_facts || []).join(" · "),
          issue_type: inferGrievanceIssueType(form),
          incident_date: form.incident_date || "",
          people_involved: buildGrievancePeople(form),
          witnesses: splitCsvGB(form.witnesses),
          timeline_description:
            `Case linked to grievance draft ${currentDraftId}${
              form.grievance_title ? ` — ${form.grievance_title}` : ""
            }`,
          // Backend's link_output already auto-advances status to grievance_drafted
          // when a grievance is linked; no explicit status_update needed.
        }}
      />
    </div>
  );
}

